import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Printer } from 'lucide-react';

export default function DiagnosticPDF() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      // Fetch all diagnostic questions
      const modules = [
        { id: 1, title: 'Module 1: Questions 1-22' },
        { id: '2A', title: 'Module 2A: Questions 23-44 (Easier)' },
        { id: '2B', title: 'Module 2B: Questions 45-66 (Harder)' }
      ];

      const allQuestions = [];
      
      for (const module of modules) {
        const response = await axios.get(`${API}/questions`, {
          params: { test_id: 'diagnostic', module: module.id }
        });
        allQuestions.push({
          module: module.title,
          questions: response.data.sort((a, b) => a.question_number - b.question_number)
        });
      }

      setQuestions(allQuestions);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching questions:', error);
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading diagnostic test questions...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Print button - hidden when printing */}
      <div className="no-print" style={{ 
        position: 'fixed', 
        top: '20px', 
        right: '20px', 
        zIndex: 1000,
        background: 'white',
        padding: '10px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        borderRadius: '8px'
      }}>
        <Button onClick={handlePrint} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Printer size={18} />
          Print to PDF
        </Button>
      </div>

      {/* PDF Content */}
      <div style={{ 
        maxWidth: '8.5in', 
        margin: '0 auto', 
        padding: '0.5in',
        background: 'white',
        fontFamily: 'Georgia, serif'
      }}>
        {/* Title */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h1 style={{ color: '#0030b3', fontSize: '28px', marginBottom: '10px' }}>
            SAT Diagnostic Test
          </h1>
          <p style={{ color: '#666', fontSize: '16px' }}>
            Complete Review - All 66 Questions with Explanations
          </p>
        </div>

        {/* Questions by Module */}
        {questions.map((moduleData, moduleIdx) => (
          <div key={moduleIdx} style={{ pageBreakBefore: moduleIdx > 0 ? 'always' : 'auto' }}>
            <div style={{ 
              background: '#0030b3', 
              color: 'white', 
              padding: '12px', 
              fontSize: '18px', 
              fontWeight: 'bold',
              marginBottom: '20px'
            }}>
              {moduleData.module}
            </div>

            {moduleData.questions.map((q, qIdx) => (
              <div key={qIdx} style={{ 
                marginBottom: '30px', 
                pageBreakInside: 'avoid',
                borderBottom: qIdx < moduleData.questions.length - 1 ? '2px solid #e5e7eb' : 'none',
                paddingBottom: '20px'
              }}>
                {/* Question Number */}
                <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#0030b3', marginBottom: '10px' }}>
                  Question {q.question_number}
                </div>

                {/* Question Text */}
                <div style={{ fontSize: '14px', marginBottom: '15px', lineHeight: '1.8' }}>
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(q.question_text) }} />
                </div>

                {/* Choices */}
                {q.choice_texts && q.choice_texts.length > 0 ? (
                  <div style={{ marginLeft: '20px', marginBottom: '15px' }}>
                    <strong>Answer Choices:</strong>
                    {q.choice_texts.map((choice, idx) => {
                      const letter = String.fromCharCode(65 + idx);
                      const isCorrect = letter === q.correct_answer;
                      return (
                        <div key={`choice-${letter}`} style={{ 
                          margin: '8px 0', 
                          fontSize: '13px',
                          color: isCorrect ? '#22c55e' : '#000',
                          fontWeight: isCorrect ? 'bold' : 'normal',
                          background: isCorrect ? '#dcfce7' : 'transparent',
                          padding: isCorrect ? '5px 10px' : '0',
                          borderRadius: isCorrect ? '4px' : '0'
                        }}>
                          {isCorrect && '✓ '}
                          {letter}. <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(choice) }} />
                          {isCorrect && ' (CORRECT)'}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ marginLeft: '20px', marginBottom: '15px' }}>
                    <span style={{ 
                      color: '#22c55e', 
                      fontWeight: 'bold',
                      background: '#dcfce7',
                      padding: '5px 10px',
                      borderRadius: '4px',
                      display: 'inline-block'
                    }}>
                      Correct Answer: {q.correct_answer}
                    </span>
                  </div>
                )}

                {/* Explanation */}
                <div style={{ marginTop: '15px' }}>
                  <strong style={{ color: '#374151', fontSize: '14px' }}>Explanation:</strong>
                  <div style={{ 
                    background: '#f9fafb', 
                    padding: '15px', 
                    borderLeft: '4px solid #0030b3',
                    marginLeft: '20px',
                    marginTop: '10px',
                    fontSize: '13px',
                    lineHeight: '1.8'
                  }}>
                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(q.explanation) }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            margin: 0;
            padding: 0;
          }
          @page {
            margin: 0.5in;
          }
        }
      `}</style>
    </div>
  );
}
